import React from 'react';
import PropTypes from 'prop-types';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { Box, Button, CircularProgress } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import CloseIcon from '@mui/icons-material/Close';

/**
 * İşlem durumunu görüntüleyen bileşen
 */
function StatusIndicator({ isLoading, error, success, message, theme }) {
    // Temayı kullan veya varsayılan değerleri ayarla
    const colors = theme?.colors || {
        status: {
            success: '#10B981',
            warning: '#FBBF24',
            error: '#EF4444',
        },
        background: {
            light: '#F9FAFB',
        },
        text: {
            primary: '#111827',
            secondary: '#4B5563',
        },
    };

    let statusColor = '';
    let statusText = '';
    let statusMessage = message || '';

    if (isLoading) {
        statusColor = colors.status.warning;
        statusText = 'Yükleniyor...';
    } else if (error) {
        statusColor = colors.status.error;
        statusText = 'Hata';
        statusMessage = statusMessage || error.toString();
    } else if (success) {
        statusColor = colors.status.success;
        statusText = 'Başarılı';
    }

    if (!statusText) return null;

    return (
        <MDBox
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: `${statusColor}10`,
                border: `1px solid ${statusColor}`,
            }}
        >
            {isLoading && <CircularProgress size={24} sx={{ color: statusColor, mr: 1.5 }} />}
            <Box>
                <MDTypography
                    variant="subtitle2"
                    sx={{
                        fontSize: '0.95rem',
                        fontWeight: 600,
                        color: statusColor,
                        mb: 0.5,
                    }}
                >
                    {statusText}
                </MDTypography>
                {statusMessage && (
                    <MDTypography
                        variant="body2"
                        sx={{
                            fontSize: '0.875rem',
                            color: colors.text.secondary,
                            lineHeight: 1.5,
                        }}
                    >
                        {statusMessage}
                    </MDTypography>
                )}
            </Box>
        </MDBox>
    );
}

/**
 * Yükleme durumunda spinner gösteren buton
 */
export function LoadingButton({
    loading,
    onClick,
    text,
    color,
    hoverColor,
    fontSize = '0.9375rem',
    padding = '8px 16px',
}) {
    return (
        <Button
            variant="contained"
            onClick={onClick}
            disabled={loading}
            sx={[{
                backgroundColor: color || '#10B981',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: fontSize,
                padding: padding,
                position: 'relative',
                '&:hover': {
                    backgroundColor: hoverColor || '#34D399',
                },
                '&:disabled': {
                    backgroundColor: '#E2E8F0',
                    color: '#64748B',
                }
            }, loading ? {
                minWidth: '160px'
            } : {
                minWidth: 'auto'
            }]}
        >
            {loading && (
                <CircularProgress
                    size={24}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        position: 'absolute',
                        left: 'calc(50% - 12px)',
                    }}
                />
            )}
            <span style={{ visibility: loading ? 'hidden' : 'visible' }}>{text}</span>
        </Button>
    );
}

StatusIndicator.propTypes = {
    isLoading: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    success: PropTypes.bool,
    message: PropTypes.string,
    theme: PropTypes.object,
};

LoadingButton.propTypes = {
    loading: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired,
    color: PropTypes.string,
    hoverColor: PropTypes.string,
    fontSize: PropTypes.string,
    padding: PropTypes.string,
};

export default StatusIndicator;
